/**
 * Fleet KPI tiles (TMMPDM-0020): currency-free counts once; money tiles one row per
 * `totals[]` currency — never summed or converted across currencies (MM-D-100).
 */
import { Box, Stack, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { KpiCard } from '@/components/ui';
import { formatMoneyWhole, formatNumber } from '@/format';
import type { CurrencyTotals } from '@/api/tmm/types';
import type { FleetData } from './useFleetDashboard';

interface FleetKpisProps {
  data: FleetData | null;
  loading: boolean;
  locale: string;
}

function MoneyRow({
  entry,
  locale,
  tenantCount,
}: {
  entry: CurrencyTotals;
  locale: string;
  tenantCount: number;
}) {
  const { t } = useCopy();
  const money = (minor: number) => formatMoneyWhole(minor, entry.currency, locale);
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {entry.currency}
      </Typography>
      <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.retailCollected', 'Retail collected')}
            value={money(entry.revenue.retailMinor)}
            hint={t('dashboard.kpi.beforeShares', 'before tenant shares')}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.wholesale', 'Wholesale')}
            value={money(entry.revenue.wholesaleMinor)}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.uplift', 'Uplift')}
            value={money(entry.revenue.upliftMinor)}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.tenantShare', 'Tenant share')}
            value={money(entry.revenue.tenantShareMinor)}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.platformShare', 'Platform share')}
            value={money(entry.revenue.platformShareMinor)}
            hint={t('dashboard.kpi.reachedUs', 'what reached us')}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.pendingPayouts', 'Pending payouts')}
            value={money(entry.payoutsPendingMinor)}
            hint={t('dashboard.kpi.paidInPeriod', '{{paid}} paid this period', {
              paid: money(entry.payoutsPaidInPeriodMinor),
            })}
          />
        </Box>
      </Stack>
      {tenantCount === 0 ? null : null}
    </Box>
  );
}

export function FleetKpis({ data, loading, locale }: FleetKpisProps) {
  const { t } = useCopy();

  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary">
        {loading ? t('common.loading', 'Loading') : null}
      </Typography>
    );
  }

  const { counts, totals, tenants } = data.dashboard;

  return (
    <Box>
      <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.activeSubscribers', 'Active subscribers')}
            value={formatNumber(counts.subscribers.active, locale)}
            hint={t('dashboard.kpi.acrossTenants', 'across {{count}} tenants', {
              count: tenants.length,
            })}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.trialing', 'Trialing')}
            value={formatNumber(counts.subscribers.trialing, locale)}
            hint={t('dashboard.kpi.newAndChurned', '{{new}} new · {{churned}} churned', {
              new: formatNumber(counts.subscribers.newInPeriod, locale),
              churned: formatNumber(counts.subscribers.churnedInPeriod, locale),
            })}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.guestSeats', "Guests' seats")}
            value={formatNumber(counts.guests.activeSeats, locale)}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.connectedAccounts', 'Connected accounts')}
            value={formatNumber(counts.connectedAccounts, locale)}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.addonsActive', 'Add-ons active')}
            value={formatNumber(counts.addonsActive, locale)}
          />
        </Box>
      </Stack>
      {totals.map((entry) => (
        <MoneyRow key={entry.currency} entry={entry} locale={locale} tenantCount={tenants.length} />
      ))}
    </Box>
  );
}
